import * as React from "react";
import { GridColDef } from "@mui/x-data-grid";
import TableFilters from "./TableFilters";
import { LoanType } from "../../types/loanType";
import { currencyFormatter } from "../../utils/utils";
import "./styles.scss";
import { useEffect, useState } from "react";
import { currentYear, currentMonth } from "../../variables/constants";
import { getInvestmentSummary } from "../../services/reportsService";
import TableRowsLoader from "../common/Loader/tableLoader";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

declare module "@mui/x-data-grid" {
  interface FooterPropsOverrides {
    total: {
      initialBalance: number;
      debits: number;
      credits: number;
      interest: number;
      balance: number;
    };
  }
}

const InvestmentTable = () => {
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [selectedMonth, setSelectedMonth] = useState<number>(currentMonth);
  const [summaryData, setSummaryData] = useState<LoanType[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const getAllTransactions = () => {
    setIsLoading(true);
    getInvestmentSummary(selectedMonth, selectedYear)
      .then((response) => {
        let sortedData = response.sort((a, b) => {
          //Then, sort by investor
          const nameA = a.investor;
          const nameB = b.investor;
          return nameA.localeCompare(nameB);
        });
        setSummaryData(sortedData);
        setIsLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getAllTransactions();
  }, []);

  useEffect(() => {
    getAllTransactions();
  }, [selectedMonth, selectedYear]);

  const columns: GridColDef[] = React.useMemo<GridColDef[]>(
    () => [
      {
        field: "investor",
        headerName: "Client",
      },

      {
        field: "account",
        headerName: "Account",
      },
      {
        field: "startingBalance",
        headerName: "Initial Balance",
      },
      {
        field: "debits",
        headerName: "Debits",
      },
      {
        field: "credits",
        headerName: "Credits",
      },
      {
        field: "interest",
        headerName: "Interest",
      },
      {
        field: "endingBalance",
        headerName: "Balance",
      },
    ],
    []
  );

  const total = React.useMemo(() => {
    const total = summaryData.reduce(
      (acc, item) => {
        acc.initialBalance += Math.round(item.startingBalance * 100);
        acc.debits += Math.round(item.debits * 100);
        acc.credits += Math.round(item.credits * 100);
        acc.interest += Math.round(item.interest * 100);
        acc.balance += Math.round(item.endingBalance * 100);
        return acc;
      },
      {
        initialBalance: 0,
        debits: 0,
        credits: 0,
        interest: 0,
        balance: 0,
      }
    );
  
    return {
      initialBalance: total.initialBalance / 100,
      debits: total.debits / 100,
      credits: total.credits / 100,
      interest: total.interest / 100,
      balance: total.balance / 100,
    };
  }, [summaryData]);
  

  return (
    <div className="data-table">
      <TableFilters
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        selectedMonth={selectedMonth}
        setSelectedMonth={setSelectedMonth}
      />
      {isLoading ? (
        <TableRowsLoader rowsNum={10} />
      ) : summaryData.length > 0 ? (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ marginTop: "15px", paddingLeft: "15px" }}
        >
          <Table className="partners-table-container" aria-label="simple table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell key={column.field} className="header-cell" align="left">
                    {column.headerName}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {summaryData.map((row) => (
                <TableRow key={row.id}>
                  <TableCell component="th" scope="row" className="table-cell">
                    {row.investor}
                  </TableCell>
                  <TableCell align="left" className="table-cell">
                    {row?.account}
                  </TableCell>
                  <TableCell align="left" className="table-cell">
                    {currencyFormatter.format(row.startingBalance)}
                  </TableCell>
                  <TableCell align="left" className="table-cell">
                    {currencyFormatter.format(row.debits)}
                  </TableCell>
                  <TableCell align="left" className="table-cell">
                    {currencyFormatter.format(row.credits)}
                  </TableCell>
                  <TableCell align="left" className="table-cell">
                    {currencyFormatter.format(row.interest)}
                  </TableCell>
                  <TableCell align="left" className="table-cell">
                    {currencyFormatter.format(row.endingBalance)}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell component="th" scope="row" className="client-header">
                  {"Total"}
                </TableCell>
                <TableCell align="left" className="client-header"></TableCell>
                <TableCell align="left" className="client-header">
                  {currencyFormatter.format(total.initialBalance)}
                </TableCell>
                <TableCell align="left" className="client-header">
                  {currencyFormatter.format(total.debits)}
                </TableCell>
                <TableCell align="left" className="client-header">
                  {currencyFormatter.format(total.credits)}
                </TableCell>
                <TableCell align="left" className="client-header">
                  {currencyFormatter.format(total.interest)}
                </TableCell>
                <TableCell align="left" className="client-header">
                  {currencyFormatter.format(total.balance)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography className="no-results">
          No Investment Summary records found
        </Typography>
      )}
    </div>
  );
};

export default InvestmentTable;
